/*
 *   Accessible Dialog Logic
 *
 *   This software or document includes material copied from or derived from https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/dialog.html.
 *   Copyright © 2018 W3C® (MIT, ERCIM, Keio, Beihang)."
 *   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 */

const CLASS_DIALOG_OPEN = 'klt-u-has-dialog';
const CLASS_BACKDROP = 'klt-u-dialog-backdrop';
const CLASS_BACKDROP_ACTIVE = 'klt-u-dialog-backdrop--active';
const CLASS_DIALOG_HIDDEN = 'klt-u-dialog--hidden';

/*
 * When util functions move focus around, set this true so the focus listener
 * can ignore the events.
 */
let ignoreUtilFocusChanges = false;
let escapeHandler = null;

/* Modals can open modals. Keep track of them with this array. */
const openDialogList = [];

export const hasOpenDialog = () => {
    return openDialogList.length > 0;
};

export class Dialog {
    dialogNode: HTMLElement;
    backdropNode: HTMLElement;
    focusAfterClosed: HTMLElement;
    focusFirst: HTMLElement;
    preNode: HTMLDivElement;
    postNode: HTMLDivElement;
    lastFocus: Element;
    closeNode: HTMLElement;

    /**
     * @constructor
     * @desc Dialog object providing modal focus management.
     *
     * Assumptions: The element serving as the dialog container is present in the
     * DOM and hidden. The dialog container has role='dialog'.
     *
     * @param dialogId
     *          The ID of the element serving as the dialog container.
     * @param focusAfterClosed
     *          Either the DOM node or the ID of the DOM node to focus when the
     *          dialog closes.
     * @param focusFirst
     *          Optional parameter containing either the DOM node or the ID of the
     *          DOM node to focus when the dialog opens. If not specified, the
     *          first focusable element in the dialog will receive focus.
     */
    constructor(
        dialogElement: string | HTMLElement,
        focusAfterClosed: HTMLElement | string,
        closeElement: string | HTMLElement = null,
        focusFirst: HTMLElement | string = null
    ) {
        if (typeof dialogElement === 'string') {
            this.dialogNode = document.querySelector(dialogElement);
        } else if (typeof focusFirst === 'object') {
            this.dialogNode = dialogElement;
        }

        const role = this.dialogNode.getAttribute('role');

        if (this.dialogNode === null || ['dialog', 'alertdialog'].indexOf(role) === -1) {
            throw new Error('Dialog() requires a DOM element with ARIA role of dialog or alertdialog.');
        }

        // Wrap in an individual backdrop element if one doesn't exist
        // Native <dialog> elements use the ::backdrop pseudo-element, which
        // works similarly.
        if ((this.dialogNode.parentNode as HTMLElement).classList.contains(CLASS_BACKDROP)) {
            this.backdropNode = this.dialogNode.parentNode as HTMLElement;
        } else {
            this.backdropNode = document.createElement('div');
            this.backdropNode.className = CLASS_BACKDROP;
            this.dialogNode.parentNode.insertBefore(this.backdropNode, this.dialogNode);
            this.backdropNode.appendChild(this.dialogNode);
        }
        this.backdropNode.classList.add(CLASS_BACKDROP_ACTIVE);
        if (this.dialogNode.classList.length > 0) {
            this.backdropNode.classList.add(`${this.dialogNode.classList[0]}-backdrop`);
        }

        // Disable scroll on the body element
        document.body.classList.add(CLASS_DIALOG_OPEN);

        if (typeof closeElement === 'string') {
            this.closeNode = document.querySelector(closeElement);
        } else if (typeof focusFirst === 'object') {
            this.closeNode = closeElement;
        }

        if (typeof focusAfterClosed === 'string') {
            this.focusAfterClosed = document.getElementById(focusAfterClosed);
        } else if (typeof focusAfterClosed === 'object') {
            this.focusAfterClosed = focusAfterClosed;
        } else {
            throw new Error('the focusAfterClosed parameter is required for the Dialog constructor.');
        }

        if (typeof focusFirst === 'string') {
            this.focusFirst = document.getElementById(focusFirst);
        } else if (typeof focusFirst === 'object') {
            this.focusFirst = focusFirst;
        }

        // Bracket the dialog node with two invisible, focusable nodes.
        // While this dialog is open, we use these to make sure that focus never
        // leaves the document even if dialogNode is the first or last node.
        const preDiv = document.createElement('div');
        this.preNode = this.dialogNode.parentNode.insertBefore(preDiv, this.dialogNode);
        this.preNode.tabIndex = 0;
        const postDiv = document.createElement('div');
        this.postNode = this.dialogNode.parentNode.insertBefore(postDiv, this.dialogNode.nextSibling);
        this.postNode.tabIndex = 0;

        // If this modal is opening on top of one that is already open,
        // get rid of the document focus listener of the open dialog.
        if (openDialogList.length > 0) {
            getCurrentDialog().removeListeners();
        }

        this.addListeners();
        openDialogList.push(this);
        this.clearDialog();
        this.dialogNode.classList.add('klt-u-dialog'); // make visible

        if (this.focusFirst) {
            this.focusFirst.focus();
        } else {
            focusFirstDescendant(this.dialogNode);
        }

        if (!escapeHandler) {
            const escapeHandler = handleEscape;
            document.addEventListener('keyup', escapeHandler);
        }

        this.lastFocus = document.activeElement;
    }

    clearDialog() {
        Array.prototype.map.call(this.dialogNode.querySelectorAll('input'), function (input) {
            input.value = '';
        });
    }

    /**
     * @desc
     *  Hides the current dialog and replaces it with another.
     *
     * @param newDialogId
     *  ID of the dialog that will replace the currently open top dialog.
     * @param newFocusAfterClosed
     *  Optional ID or DOM node specifying where to place focus when the new dialog closes.
     *  If not specified, focus will be placed on the element specified by the dialog being replaced.
     * @param newFocusFirst
     *  Optional ID or DOM node specifying where to place focus in the new dialog when it opens.
     *  If not specified, the first focusable element will receive focus.
     */
    replace(newDialogId, newFocusAfterClosed, newFocusFirst) {
        const closedDialog = getCurrentDialog();
        openDialogList.pop();
        this.removeListeners();
        remove(this.preNode);
        remove(this.postNode);
        this.dialogNode.classList.add(CLASS_DIALOG_HIDDEN);
        this.backdropNode.classList.remove(CLASS_BACKDROP_ACTIVE);

        const focusAfterClosed = newFocusAfterClosed || this.focusAfterClosed;
        const dialog = new Dialog(newDialogId, focusAfterClosed, newFocusFirst);
    }

    addListeners() {
        document.addEventListener('focus', this.trapFocus, true);
        if (this.closeNode) {
            this.closeNode.addEventListener('click', this.close.bind(this));
        }

        if (this.backdropNode) {
            this.backdropNode.addEventListener('click', (event) => this.backgroundClickHandler(event));
        }
    }

    removeListeners() {
        document.removeEventListener('focus', this.trapFocus, true);
        if (this.closeNode) {
            this.closeNode.removeEventListener('click', this.close.bind(this));
        }
    }

    /**
     * @desc
     *  Hides the current top dialog,
     *  removes listeners of the top dialog,
     *  restore listeners of a parent dialog if one was open under the one that just closed,
     *  and sets focus on the element specified for focusAfterClosed.
     */
    close() {
        openDialogList.pop();
        this.removeListeners();
        remove(this.preNode);
        remove(this.postNode);
        this.dialogNode.classList.remove(CLASS_DIALOG_HIDDEN);
        this.backdropNode.classList.remove(CLASS_BACKDROP_ACTIVE);
        this.focusAfterClosed.focus();

        // If a dialog was open underneath this one, restore its listeners.
        if (openDialogList.length > 0) {
            getCurrentDialog().addListeners();
        } else {
            document.body.classList.remove(CLASS_DIALOG_OPEN);
        }
    }

    trapFocus(event) {
        if (ignoreUtilFocusChanges) {
            return;
        }
        const currentDialog = getCurrentDialog();
        if (currentDialog.dialogNode.contains(event.target)) {
            currentDialog.lastFocus = event.target;
        } else {
            focusFirstDescendant(currentDialog.dialogNode);
            if (currentDialog.lastFocus == document.activeElement) {
                focusLastDescendant(currentDialog.dialogNode);
            }
            currentDialog.lastFocus = document.activeElement;
        }
    }

    backgroundClickHandler(event: Event) {
        if (event.target === this.dialogNode) {
            this.close();
        }
    }
}

/**
 * @desc Set focus on descendant nodes until the first focusable element is
 *       found.
 * @param element DOM node for which to find the first focusable descendant.
 * @returns true if a focusable element is found and focus is set.
 */
function focusFirstDescendant(element: HTMLElement | ChildNode): boolean {
    for (let i = 0; i < element.childNodes.length; i++) {
        const child = element.childNodes[i];
        if (attemptFocus(child) || focusFirstDescendant(child)) {
            return true;
        }
    }
    return false;
}

/**
 * @desc Find the last descendant node that is focusable.
 * @param element DOM node for which to find the last focusable descendant.
 * @returns true if a focusable element is found and focus is set.
 */
function focusLastDescendant(element: HTMLElement | ChildNode): boolean {
    for (let i = element.childNodes.length - 1; i >= 0; i--) {
        const child = element.childNodes[i];
        if (attemptFocus(child) || focusLastDescendant(child)) {
            return true;
        }
    }
    return false;
}

/**
 * @desc Set Attempt to set focus on the current node.
 * @param element The node to attempt to focus on.
 * @returns true if element is focused.
 */
function attemptFocus(element): boolean {
    if (!isFocusable(element)) {
        return false;
    }

    ignoreUtilFocusChanges = true;

    try {
        element.focus();
    } catch (e) {}

    ignoreUtilFocusChanges = false;

    return document.activeElement === element;
}

function isFocusable(element: HTMLElement): boolean {
    if (
        element.hidden ||
        (element.style && element.style.display === 'none') ||
        (element as HTMLButtonElement).disabled === true ||
        (element.tabIndex && element.tabIndex === -1)
    ) {
        return false;
    }

    return true;
}

/**
 * @returns the last opened dialog (the current dialog)
 */
function getCurrentDialog() {
    if (openDialogList && openDialogList.length) {
        return openDialogList[openDialogList.length - 1];
    }
}

function closeCurrentDialog() {
    const currentDialog = getCurrentDialog();
    if (currentDialog) {
        currentDialog.close();
        return true;
    }

    return false;
}

function handleEscape(event) {
    const key = event.which || event.keyCode;

    if (key === 27 && closeCurrentDialog()) {
        event.stopPropagation();
    }
}

function openDialog(dialogId: string, focusAfterClosed, focusFirst) {
    const dialog = new Dialog(dialogId, focusAfterClosed, focusFirst);
}

function closeDialog(closeButton: HTMLElement) {
    const topDialog = getCurrentDialog();
    if (topDialog.dialogNode.contains(closeButton)) {
        topDialog.close();
    }
}

function replaceDialog(newDialogId, newFocusAfterClosed, newFocusFirst) {
    const topDialog = getCurrentDialog();
    if (topDialog.dialogNode.contains(document.activeElement)) {
        topDialog.replace(newDialogId, newFocusAfterClosed, newFocusFirst);
    }
}

function remove(element: HTMLElement) {
    if (element.remove) {
        element.remove();
    } else {
        if (element.parentNode !== null) {
            element.parentNode.removeChild(element);
        }
    }
}
